import type { GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { useLink } from '~/hooks/link';
import { useOrderByParams } from '~/hooks/order-by';
import EmptyIllustration from '~/images/illustrations/upload-media.svg';
import type { FontList__Font as Font } from '../../queries/list.generated';
import { useColumns } from '../lib';

const NoRowsOverlay = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      illustration={EmptyIllustration}
      description={t('pages.fonts.empty_state.description')}
      header={t('pages.fonts.empty_state.header')}
    />
  );
};

export interface TableProps {
  fonts: readonly Font[];
  loading: boolean;
  onDelete: (font: Font) => void;
}

export const Table = ({ fonts, loading, onDelete }: TableProps) => {
  const columns = useColumns({ onDelete });
  const navigate = useNavigate();
  const link = useLink();

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  return (
    <ScrollableDataGrid
      columns={columns}
      loading={loading}
      onRowClick={({ row }: GridRowParams<Font>) => navigate(link(`/fonts/${row.id}`))}
      rows={fonts}
      slots={{
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};
